import { Trans, useLingui } from '@lingui/react/macro';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { FeedbackTargetValues } from 'src/api/endpoints/storeApi.types';
import { usePaymentHistory, useStripeSubscription } from 'src/api/queries';
import { PaymentHistoryFAQ } from 'src/apps/manageSubscription/app/components/PaymentHistoryFAQ';
import {
  Divider,
  FaqSection,
  Feedback,
  NotificationBox,
  Sheet,
} from 'src/components';
import { WithStripeElements } from 'src/components/WithStripeElements';
import { FAQ } from 'src/constants/FAQ';

import { Boundary } from 'src/components/Boundary';
import { BillingInfoCard } from '../components/BillingInfoCard';
import { ChangePaymentInterval } from '../components/ChangePaymentInterval';
import { NextBillingCard } from '../components/NextBillingCard';
import { PaymentHistoryCard } from '../components/PaymentHistoryCard';
import { PaymentMethod } from '../components/PaymentMethod';

export const PaymentPage = () => {
  const { t } = useLingui();
  const subscription = useStripeSubscription();
  const isSeatsSubscription = subscription.data?.managementType === 'seats';
  const paymentHistory = usePaymentHistory();
  const hasPaymentAccess =
    subscription.data?.access.canChangePaymentInformation ||
    subscription.data?.access.canSeeNextInvoiceAmount ||
    subscription.data?.access.canSeeFailedPaymentInformation ||
    subscription.data?.access.canSeePaymentHistory ||
    subscription.data?.access.canSeePaymentInformation;

  // If there is a subscription, we want to check access, otherwise any payment history is fine
  const canSeePaymentHistory = (() => {
    if (!subscription.isSuccess) {
      return false;
    }

    if (!subscription.data) {
      return !!paymentHistory.data?.length;
    }

    return subscription.data.access.canSeePaymentHistory;
  })();

  const feedbackTargetId = useMemo((): FeedbackTargetValues | null => {
    if (!subscription.data) return null;

    if (subscription.data.managementType === 'simple') {
      return 'connect-payment-page-v1';
    }

    if (subscription.data.access.canSeeMembers) {
      return 'c4b-payment-page-for-owner-v1';
    }

    return 'c4b-payment-page-for-members-v1';
  }, [subscription.data]);

  const shouldSeeChangePaymentInterval =
    subscription.data?.access.canChangePaymentInterval;

  useEffect(() => {
    if (shouldSeeChangePaymentInterval) return;

    Sentry.setTags({
      'rm.canChangePaymentInterval': String(
        subscription.data?.access.canChangePaymentInterval
      ),
    });

    Sentry.captureEvent({
      message: 'Change payment interval not available to user',
      level: 'info',
      extra: {
        canChange: subscription.data?.access.canChangePaymentInterval,
      },
    });
  }, [shouldSeeChangePaymentInterval]);

  return (
    <div className="flex flex-col gap-24 lm:gap-32">
      {subscription.data?.readonly && (
        <NotificationBox
          title={t`You are unable to make changes your subscription due to system maintenance.`}
          variant="warning"
        >
          <Trans>Please try again later.</Trans>
        </NotificationBox>
      )}

      {subscription.data?.access.canSeeNextInvoiceAmount && <NextBillingCard />}

      {shouldSeeChangePaymentInterval && (
        <ChangePaymentInterval className="gap-24" />
      )}

      <Sheet className="gap-24">
        {subscription.data?.access.canChangePaymentInformation && (
          <>
            <WithStripeElements>
              <PaymentMethod />
            </WithStripeElements>
            <Divider />
          </>
        )}
        <Boundary>
          <BillingInfoCard />
        </Boundary>
      </Sheet>

      {canSeePaymentHistory && (
        <>
          <PaymentHistoryCard />
        </>
      )}

      {isSeatsSubscription && hasPaymentAccess ? (
        <FaqSection
          className="my-96"
          componentLocation={ComponentLocations.SUBSCRIPTION.PAYMENT_PAGE}
          sections={[
            FAQ.connectWithSeats.whatPaymentOptionsDoYouOffer(),
            FAQ.connectWithSeats.whyWasMyPaymentDeclined(),
            FAQ.connectWithSeats.howOftenWillIBeChargedForConnectWithSeats(),
            FAQ.connectWithSeats.whyWasMyBillingDifferentThanExpected(),
          ]}
        />
      ) : (
        <PaymentHistoryFAQ />
      )}

      {feedbackTargetId && (
        <Feedback
          feedbackTargetId={feedbackTargetId}
          title={t`Did you find what you were looking for?`}
        />
      )}
    </div>
  );
};
